import {
  createColumnHelper,
  ExpandedState,
  getCoreRowModel,
  useReactTable
} from "@tanstack/react-table";
import { FC, useContext, useEffect, useState } from "react";
import { Table } from "shared/app/Table";
import {
  Badge,
  BadgeSize,
  BadgeVariant,
  Button,
  ButtonSize,
  ButtonVariant
} from "shared/components";
import { cx } from "shared/utils";
import { CriminalReportOffense } from "types/landlord/reports";
import { ChevronDown } from "untitledui-js-base";
import { ContentTitle } from "views/applications-id-report/components/ContentTitle";
import { DynamicKeyValue } from "views/applications-id-report/components/SmartRender/DynamicKeyValue";
import { PrintAllContext } from "views/applications-id-report/providers/PrintProvider";
import { formatDate } from "views/applications-id-report/utils";
import { KeyPairWrapper } from "./KeyPairWrapper";

type OffensesTableProps = {
  offenses: CriminalReportOffense[];
};

export const OffensesTable: FC<OffensesTableProps> = ({ offenses }) => {
  const [expanded, setExpanded] = useState<ExpandedState>({});

  const { documentPrintingState } = useContext(PrintAllContext);

  const columnHelper = createColumnHelper<CriminalReportOffense>();

  const columns = [
    columnHelper.accessor("description", {
      header: "Offense",
      cell: ({ getValue }) => {
        const data = getValue();
        return (
          <div className="flex items-center gap-2">
            <img
              src="/assets/criminal_report_logo.svg"
              alt="Criminal report icon"
            />
            <p className="truncate text-content-1000 text-sm font-medium max-w-[480px]">
              {data}
            </p>
          </div>
        );
      }
    }),
    columnHelper.accessor("categorization", {
      header: "",
      size: 80,
      cell: ({ getValue }) => {
        const data = getValue();

        return (
          <Badge
            className="shrink-0 items-center capitalize"
            size={BadgeSize.SM}
            variant={BadgeVariant.ERROR}
          >
            {data.replaceAll("_", " ")}
          </Badge>
        );
      }
    }),
    columnHelper.accessor("offense_date", {
      header: "Offense date",
      size: 80,
      cell: ({ getValue }) => {
        const data = getValue();
        return (
          <p className="truncate text-content-1000 text-sm font-medium">
            {formatDate(data)}
          </p>
        );
      }
    }),
    columnHelper.accessor("disposition", {
      size: 80,
      header: "Disposition",
      cell: ({ getValue }) => {
        const data = getValue();
        return (
          <p className="truncate text-content-1000 text-sm font-medium">
            {data}
          </p>
        );
      }
    }),
    columnHelper.accessor("expander" as any, {
      header: "",
      id: "fixed_width",
      size: 64,
      cell: ({ row }) => {
        return (
          row.getCanExpand() && (
            <Button
              variant={ButtonVariant.BASE}
              size={ButtonSize.SM}
              onClick={() => row.toggleExpanded()}
              borderLess
            >
              <div className="flex items-center gap-2">
                <ChevronDown
                  className={cx(
                    "stroke-content-300",
                    row.getIsExpanded() ? "rotate-180 transition-all" : ""
                  )}
                  size="20"
                />
              </div>
            </Button>
          )
        );
      }
    })
  ];

  const table = useReactTable({
    state: {
      expanded: Boolean(documentPrintingState?.length) || expanded
    },
    data: offenses,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    onExpandedChange: setExpanded,
    getRowCanExpand: () => true
  });

  useEffect(() => {
    if (documentPrintingState?.length) setExpanded(true);
  }, [documentPrintingState]);

  return (
    <Table
      table={table}
      thClassName="!px-2"
      tdClassName="!px-2"
      borderLess
      expandedRenderer={(row) => {
        const offense = row.original as CriminalReportOffense;

        return (
          <div className="px-4 py-2">
            <ContentTitle title="Offense">
              <KeyPairWrapper>
                <DynamicKeyValue detailsKey="Court" value={offense.court} />
              </KeyPairWrapper>
              <KeyPairWrapper>
                <DynamicKeyValue
                  detailsKey="Case Type"
                  value={offense.case_type}
                />
              </KeyPairWrapper>

              <KeyPairWrapper>
                <DynamicKeyValue detailsKey="Status" value={offense.status} />
              </KeyPairWrapper>

              <KeyPairWrapper>
                <DynamicKeyValue
                  detailsKey="Filing Date"
                  value={
                    offense.filing_date ? formatDate(offense.filing_date) : "--"
                  }
                />
              </KeyPairWrapper>
              <KeyPairWrapper>
                <DynamicKeyValue
                  detailsKey="Description"
                  value={offense.description}
                />
              </KeyPairWrapper>

              <KeyPairWrapper hide={!offense.statute}>
                <DynamicKeyValue detailsKey="Statute" value={offense.statute} />
              </KeyPairWrapper>

              <KeyPairWrapper hide={!offense.conviction_date}>
                <DynamicKeyValue
                  detailsKey="Conviction Date"
                  value={formatDate(offense.conviction_date)}
                />
              </KeyPairWrapper>

              <KeyPairWrapper hide={!offense.conviction_location}>
                <DynamicKeyValue
                  detailsKey="Conviction Location"
                  value={offense.conviction_location}
                />
              </KeyPairWrapper>

              <KeyPairWrapper hide={!offense.disposition}>
                <DynamicKeyValue
                  detailsKey="Disposition"
                  value={offense.disposition}
                />
              </KeyPairWrapper>

              <KeyPairWrapper hide={!offense.disposition_date}>
                <DynamicKeyValue
                  detailsKey="Disposition Date"
                  value={formatDate(offense.disposition_date)}
                />
              </KeyPairWrapper>

              <KeyPairWrapper hide={!offense.offense_code}>
                <DynamicKeyValue
                  detailsKey="Offense Code"
                  value={offense.offense_code}
                />
              </KeyPairWrapper>

              <KeyPairWrapper hide={!offense.offense_date}>
                <DynamicKeyValue
                  detailsKey="Offense Date"
                  value={formatDate(offense.offense_date)}
                />
              </KeyPairWrapper>

              <KeyPairWrapper hide={!offense.charge_file_date}>
                <DynamicKeyValue
                  detailsKey="Charge file date"
                  value={formatDate(offense.charge_file_date)}
                />
              </KeyPairWrapper>

              <KeyPairWrapper hide={!offense.sentence_length}>
                <DynamicKeyValue
                  detailsKey="Sentence Length"
                  value={offense.sentence_length}
                />
              </KeyPairWrapper>

              <KeyPairWrapper hide={!offense.commitment_date}>
                <DynamicKeyValue
                  detailsKey="Commitment Date"
                  value={formatDate(offense.commitment_date)}
                />
              </KeyPairWrapper>

              <KeyPairWrapper hide={!offense.release_date}>
                <DynamicKeyValue
                  detailsKey="Release Date"
                  value={formatDate(offense.release_date)}
                />
              </KeyPairWrapper>
            </ContentTitle>
          </div>
        );
      }}
    />
  );
};
